/* OpenFOAM decomposeParDict blocks – specialized for domain decomposition */
import * as Blockly from 'blockly';

// Constants
const PREFIX = 'decomposeParDict';
const BLOCK_COLOR = 200; // Base color for decomposeParDict blocks

// Consistent color scheme
const COLORS = {
  BASE: BLOCK_COLOR,              // 200 - Root blocks (dict, foamfile)
  VALUE: BLOCK_COLOR,         // 205 - Basic value blocks
  DROPDOWN: BLOCK_COLOR,     // 210 - All dropdown blocks
  METHOD: BLOCK_COLOR,       // 215 - Decomposition methods
  COEFFS: BLOCK_COLOR,       // 220 - Method coefficients
  DISTRIBUTED: BLOCK_COLOR,  // 225 - Distributed settings
  GEOMETRY: BLOCK_COLOR     // 230 - Geometric constraints
};

// Default FoamFile entries for decomposeParDict
const DEFAULT_FOAMFILE_ENTRIES = {
  version: '2.0',
  format: 'ascii',
  class: 'dictionary',
  location: 'system',
  object: 'decomposeParDict'
};

// Define validation functions for different decomposition parameters
const VALIDATORS = {
  numberOfSubdomains: (value) => {
    const num = parseInt(value);
    return !isNaN(num) && num > 0 ? num : 4;
  },
  
  method: (value) => {
    const validMethods = ['simple', 'hierarchical', 'scotch', 'metis', 'manual', 'structured'];
    return validMethods.includes(value) ? value : 'scotch';
  },
  
  distributed: (value) => {
    const validValues = ['true', 'false', 'yes', 'no'];
    return validValues.includes(value.toLowerCase()) ? value.toLowerCase() : 'false';
  },
  
  decomposer: (value) => {
    const validDecomposers = ['scotch', 'metis', 'hierarchical', 'simple', 'manual', 'structured', 'multiLevel'];
    return validDecomposers.includes(value) ? value : 'scotch';
  },
  
  distributor: (value) => {
    const validDistributors = ['distributed', 'simple', 'ptscotch', 'parMetis'];
    return validDistributors.includes(value) ? value : 'distributed';
  },
  
  // Simple method coefficients
  simpleCoeffs_n: (value) => {
    // Expects format like "(2 2 1)" for 3D decomposition
    if (typeof value === 'string' && value.includes('(') && value.includes(')')) {
      return value;
    }
    return '(2 2 1)';
  },
  
  simpleCoeffs_delta: (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= 0 ? num : 0.001;
  },
  
  // Hierarchical method coefficients
  hierarchicalCoeffs_n: (value) => {
    if (typeof value === 'string' && value.includes('(') && value.includes(')')) {
      return value;
    }
    return '(2 2 1)';
  },
  
  hierarchicalCoeffs_delta: (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= 0 ? num : 0.001;
  },
  
  hierarchicalCoeffs_order: (value) => {
    const validOrders = ['xyz', 'xzy', 'yxz', 'yzx', 'zxy', 'zyx'];
    return validOrders.includes(value) ? value : 'xyz';
  },
  
  // Metis/Scotch coefficients
  processorWeights: (value) => {
    // Expects format like "(1 1 1 1)" for processor weights
    if (typeof value === 'string' && value.includes('(') && value.includes(')')) {
      return value;
    }
    return '(1 1 1 1)';
  }
};

// Define decomposition method configurations
const METHOD_CONFIGS = {
  simple: [
    { 
      key: 'n', 
      shadow: `${PREFIX}_decomp_vector`, 
      check: 'Vector3', 
      tooltip: 'Number of subdivisions in each direction (x y z) - specialized decomposition vector',
      validator: VALIDATORS.simpleCoeffs_n,
      defaultValue: '(4 2 1)',
      isVector3: true
    },
    { 
      key: 'delta', 
      shadow: `${PREFIX}_value_number`, 
      check: 'Number', 
      tooltip: 'Cell skew tolerance',
      validator: VALIDATORS.simpleCoeffs_delta,
      defaultValue: 0.001
    }
  ],
  
  hierarchical: [
    { 
      key: 'n', 
      shadow: `${PREFIX}_decomp_vector`, 
      check: 'Vector3', 
      tooltip: 'Number of subdivisions in each direction (x y z) - specialized decomposition vector',
      validator: VALIDATORS.hierarchicalCoeffs_n,
      defaultValue: '(4 2 1)',
      isVector3: true
    },
    { 
      key: 'delta', 
      shadow: `${PREFIX}_value_number`, 
      check: 'Number', 
      tooltip: 'Cell skew tolerance',
      validator: VALIDATORS.hierarchicalCoeffs_delta,
      defaultValue: 0.001
    },
    { 
      key: 'order', 
      shadow: `${PREFIX}_axis_order_dropdown`, 
      check: 'String', 
      tooltip: 'Order of decomposition (xyz, xzy, etc.)',
      validator: VALIDATORS.hierarchicalCoeffs_order,
      defaultValue: 'xyz'
    }
  ],
  
  scotch: [
    { 
      key: 'processorWeights', 
      shadow: `${PREFIX}_decomp_vector`, 
      check: 'Vector3', 
      tooltip: 'Processor weights for load balancing - specialized decomposition vector',
      validator: VALIDATORS.processorWeights,
      defaultValue: '(1 1 1)',
      optional: true
    }
  ],
  
  metis: [
    { 
      key: 'processorWeights', 
      shadow: `${PREFIX}_decomp_vector`, 
      check: 'Vector3', 
      tooltip: 'Processor weights for load balancing - specialized decomposition vector',
      validator: VALIDATORS.processorWeights,
      defaultValue: '(1 1 1)',
      optional: true
    }
  ],
  
  manual: [],
  
  structured: [
    { 
      key: 'patches', 
      shadow: `${PREFIX}_patch_list`, 
      check: 'List', 
      tooltip: 'List of patches for structured decomposition',
      validator: (value) => value || '()',
      defaultValue: '()'
    },
    { 
      key: 'method', 
      shadow: `${PREFIX}_method_dropdown`, 
      check: 'String', 
      tooltip: 'Underlying decomposition method',
      validator: VALIDATORS.method,
      defaultValue: 'scotch'
    }
  ]
};

// Define main keyword configurations
const KEYWORD_CONFIGS = [
  { 
    key: 'numberOfSubdomains', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Total number of processor subdomains',
    validator: VALIDATORS.numberOfSubdomains,
    defaultValue: 4
  },
  { 
    key: 'method', 
    shadow: `${PREFIX}_method_dropdown`, 
    check: 'String', 
    tooltip: 'Domain decomposition method',
    validator: VALIDATORS.method,
    defaultValue: 'scotch'
  },
  { 
    key: 'decomposer', 
    shadow: `${PREFIX}_method_dropdown`, 
    check: 'String', 
    tooltip: 'Decomposer algorithm for domain partitioning',
    validator: VALIDATORS.decomposer,
    defaultValue: 'scotch'
  },
  { 
    key: 'distributor', 
    shadow: `${PREFIX}_distributor_dropdown`, 
    check: 'String', 
    tooltip: 'Distribution method for parallel processing',
    validator: VALIDATORS.distributor,
    defaultValue: 'distributed'
  },
  { 
    key: 'distributed', 
    shadow: `${PREFIX}_boolean_dropdown`, 
    check: 'String', 
    tooltip: 'Whether the case is distributed across multiple machines',
    validator: VALIDATORS.distributed,
    defaultValue: 'false'
  },
  { 
    key: 'roots', 
    shadow: `${PREFIX}_machine_list`, 
    check: 'List', 
    tooltip: 'List of machine names for distributed cases',
    validator: (value) => value || '()',
    defaultValue: '()'
  }
];

/**
 * Utility to create and initialize a Blockly block
 */
function createBlock(workspace, blockType) {
  try {
    if (!Blockly.Blocks[blockType]) {
      throw new Error(`Block type ${blockType} not defined`);
    }
    const block = workspace.newBlock(blockType);
    block.initSvg();
    block.render();
    return block;
  } catch (error) {
    console.error(`Error creating block ${blockType}:`, error);
    return null;
  }
}

/**
 * Utility to connect a value block to a block's input
 */
function connectValueBlock(block, valueBlock, entryKey) {
  try {
    const valueInput = block.getInput('VALUE') || block.getInput(entryKey);
    if (!valueInput) {
      console.warn(`No VALUE or ${entryKey} input found for block with key ${entryKey}`);
      return false;
    }

    if (!valueBlock || !valueBlock.outputConnection) {
      console.warn(`No valid output connection for value block of ${entryKey}`);
      return false;
    }

    const originalCheck = valueInput.connection.getCheck();
    valueInput.connection.setCheck(null);
    valueInput.connection.connect(valueBlock.outputConnection);
    valueInput.connection.setCheck(originalCheck);
    console.log(`Connected value block for ${entryKey}`);
    return true;
  } catch (error) {
    console.error(`Failed to connect value block for ${entryKey}:`, error);
    return false;
  }
}

// Register all blocks for this dictionary
export function registerBlocks(params = {}) {
  try {
    const filename = params.filename || 'decomposeParDict';
    
    console.log(`Registering decomposeParDict-specific blocks for ${filename}`);
    registerRootBlock(filename);
    registerKeywordBlocks(filename);
    registerMethodBlocks(filename);
    registerFoamFileBlock(filename);
    registerValueBlocks(filename);
    registerDropdownBlocks(filename);
    console.log(`Registered ${filename} blocks:`, Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)));
  } catch (error) {
    console.error(`Error registering blocks for ${params.filename || 'decomposeParDict'}:`, error);
  }
}

// Register the root block
function registerRootBlock(filename) {
  const id = `${PREFIX}_dict`;
  if (Blockly.Blocks[id]) {
    console.log(`Root block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;
      this.jsonInit({
        type: id,
        message0: '%1 %2',
        args0: [
          {
            type: 'field_label',
            name: 'DICT_NAME',
            text: filename
          },
          {
            type: 'field_label',
            name: 'FILENAME',
            text: `(${filename})`
          }
        ],
        inputsInline: true,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `Root dictionary for ${filename}`,
        helpUrl: ''
      });
      
      this.appendStatementInput('ENTRIES')
        .setCheck(null)
        .appendField('entries');
    }
  };
  
  console.log(`Registered root block: ${id}`);
}

// Register main keyword blocks
function registerKeywordBlocks(filename) {
  // Register decomposeParDict-specific number block
  const numberBlockType = `${PREFIX}_value_number`;
  if (!Blockly.Blocks[numberBlockType]) {
    Blockly.Blocks[numberBlockType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: numberBlockType,
          message0: '%1',
          args0: [
            {
              type: 'field_number',
              name: 'VALUE',
              value: 1,
              min: 1,
              validator: function(newValue) {
                const num = Number(newValue);
                return !isNaN(num) && num >= 1 ? num : 1;
              }
            }
          ],
          output: 'Number',
          colour: COLORS.VALUE,
          tooltip: `Number value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Register keyword blocks
  KEYWORD_CONFIGS.forEach(({ key, shadow, check, tooltip, validator, defaultValue }) => {
    const id = `${PREFIX}_${key}`;
    if (Blockly.Blocks[id]) {
      console.log(`Keyword block ${id} already registered`);
      return;
    }

    Blockly.Blocks[id] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: id,
          message0: '🔧 %1 %2',
          args0: [
            {
              type: 'field_label',
              name: 'KEY',
              text: key
            },
            {
              type: 'input_value',
              name: 'VALUE',
              check: check
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.BASE,
          tooltip: `${tooltip} (${filename})`,
          helpUrl: '',
          inputsInline: true
        });
      }
    };

    console.log(`Registered keyword block: ${id} for ${filename}`);
  });
}

// Register method-specific coefficient blocks
function registerMethodBlocks(filename) {
  Object.entries(METHOD_CONFIGS).forEach(([methodName, coeffs]) => {
    // Register the method coefficients section block
    const methodSectionId = `${PREFIX}_${methodName}Coeffs_section`;
    if (!Blockly.Blocks[methodSectionId]) {
      Blockly.Blocks[methodSectionId] = {
        init: function() {
          this.filename = filename;
          
          // Get method-specific visual indicators
          const methodInfo = this.getMethodInfo(methodName);
          
          this.jsonInit({
            type: methodSectionId,
            message0: '%1 %2Coeffs',
            args0: [
              {
                type: 'field_label',
                name: 'METHOD_ICON',
                text: methodInfo.icon
              },
              {
                type: 'field_label',
                name: 'METHOD_NAME',
                text: methodName
              }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: methodInfo.color,
            tooltip: `${methodInfo.description} method coefficients`,
            helpUrl: ''
          });
          
          this.appendStatementInput('COEFFS')
            .setCheck(null)
            .appendField('coefficients');
        },
        
        getMethodInfo: function(methodName) {
          const methodMap = {
            simple: {
              icon: '📐',
              color: COLORS.METHOD,
              description: 'Simple geometric decomposition'
            },
            hierarchical: {
              icon: '🌳',
              color: COLORS.METHOD + 5,
              description: 'Hierarchical decomposition'
            },
            scotch: {
              icon: '⚡',
              color: COLORS.METHOD + 10,
              description: 'Scotch graph partitioning'
            },
            metis: {
              icon: '🔬',
              color: COLORS.METHOD + 15,
              description: 'METIS graph partitioning'
            },
            manual: {
              icon: '✋',
              color: COLORS.METHOD + 20,
              description: 'Manual domain assignment'
            },
            structured: {
              icon: '🏗️',
              color: COLORS.METHOD + 25,
              description: 'Structured decomposition'
            }
          };
          return methodMap[methodName] || { icon: '?', color: COLORS.METHOD, description: 'Unknown method' };
        }
      };
      
      console.log(`Registered method section block: ${methodSectionId}`);
    }

    // Register individual coefficient blocks for this method
    coeffs.forEach(config => {
      const coeffId = `${PREFIX}_${methodName}Coeffs_${config.key}`;
      if (Blockly.Blocks[coeffId]) return;

      Blockly.Blocks[coeffId] = {
        init: function() {
          this.filename = filename;
          this.jsonInit({
            type: coeffId,
            message0: '⚙️ %1 %2',
            args0: [
              {
                type: 'field_label',
                name: 'KEY',
                text: config.key
              },
              {
                type: 'input_value',
                name: 'VALUE',
                check: config.check
              }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: COLORS.COEFFS,
            tooltip: `${config.tooltip} (${methodName} method)`,
            inputsInline: true
          });
        }
      };
      
      console.log(`Registered coefficient block: ${coeffId}`);
    });
    
    // Register a generic keyValue block for this method's coefficients
    const genericCoeffId = `${PREFIX}_${methodName}Coeffs_keyValue`;
    if (!Blockly.Blocks[genericCoeffId]) {
      Blockly.Blocks[genericCoeffId] = {
        init: function() {
          this.filename = filename;
          this.jsonInit({
            type: genericCoeffId,
            message0: '🔧 %1 %2',
            args0: [
              {
                type: 'field_input',
                name: 'KEY',
                text: 'key'
              },
              {
                type: 'input_value',
                name: 'VALUE',
                check: ['String', 'Number', 'Vector3', 'List']
              }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: COLORS.COEFFS,
            tooltip: `Generic key-value entry for ${methodName} coefficients`,
            inputsInline: true
          });
        }
      };
      
      console.log(`Registered generic coefficient keyValue block: ${genericCoeffId}`);
    }
  });
}

// Register FoamFile block
function registerFoamFileBlock(filename) {
  const id = `${PREFIX}_foamfile`;
  if (Blockly.Blocks[id]) {
    console.log(`FoamFile block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;
      this.jsonInit({
        type: id,
        message0: 'FoamFile',
        message1: 'version %1',
        args1: [
          {
            type: 'input_value',
            name: 'VERSION',
            check: 'String'
          }
        ],
        message2: 'format %1',
        args2: [
          {
            type: 'input_value',
            name: 'FORMAT',
            check: 'String'
          }
        ],
        message3: 'class %1',
        args3: [
          {
            type: 'input_value',
            name: 'CLASS',
            check: 'String'
          }
        ],
        message4: 'location %1',
        args4: [
          {
            type: 'input_value',
            name: 'LOCATION',
            check: 'String'
          }
        ],
        message5: 'object %1',
        args5: [
          {
            type: 'input_value',
            name: 'OBJECT',
            check: 'String'
          }
        ],
        previousStatement: null,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `FoamFile dictionary for ${filename}`,
        helpUrl: '',
      });

      // Set shadow blocks
      const fields = ['VERSION', 'FORMAT', 'CLASS', 'LOCATION', 'OBJECT'];
      const parser = new DOMParser();
      fields.forEach(field => {
        const input = this.getInput(field);
        if (input) {
          const shadowXmlString = `
            <shadow type="${PREFIX}_value_string">
              <field name="VALUE">${DEFAULT_FOAMFILE_ENTRIES[field.toLowerCase()]}</field>
            </shadow>
          `;
          const shadowXml = parser.parseFromString(shadowXmlString, 'text/xml').documentElement;
          input.connection.setShadowDom(shadowXml);
          const shadowBlock = input.connection.targetBlock();
          if (shadowBlock) {
            shadowBlock.filename = filename;
          }
        }
      });
    },
  };

  console.log(`Registered FoamFile block: ${id} for ${filename}`);
}

// Register specialized value blocks
function registerValueBlocks(filename) {
  // String value block
  const valueStringType = `${PREFIX}_value_string`;
  if (!Blockly.Blocks[valueStringType]) {
    Blockly.Blocks[valueStringType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueStringType,
          message0: '❝ %1 ❞',
          args0: [
            {
              type: 'field_input',
              name: 'VALUE',
              text: ''
            }
          ],
          output: 'String',
          colour: COLORS.VALUE,
          tooltip: `String value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Number value block for decomposeParDict
  const valueNumberType = `${PREFIX}_value_number`;
  if (!Blockly.Blocks[valueNumberType]) {
    Blockly.Blocks[valueNumberType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueNumberType,
          message0: '# %1',
          args0: [
            {
              type: 'field_number',
              name: 'VALUE',
              value: 1,
              min: 1,
              precision: 1
            }
          ],
          output: 'Number',
          colour: COLORS.VALUE,
          tooltip: `Integer value for ${filename} (minimum: 1)`,
          editable: true
        });
      }
    };
  }



  // Specialized decomposition vector block with enhanced features
  const decompVectorType = `${PREFIX}_decomp_vector`;
  if (!Blockly.Blocks[decompVectorType]) {
    Blockly.Blocks[decompVectorType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: decompVectorType,
          message0: '🔷 ( %1 %2 %3 )',
          args0: [
            {
              type: 'input_value',
              name: 'X',
              check: 'Number'
            },
            {
              type: 'input_value',
              name: 'Y',
              check: 'Number'
            },
            {
              type: 'input_value',
              name: 'Z',
              check: 'Number'
            }
          ],
          output: 'Vector3',
          colour: COLORS.VALUE + 10,
          tooltip: 'Specialized decomposition vector for domain partitioning (x y z)\nTypically used for n-coefficients, delta values, etc.',
          inputsInline: true
        });
        
        // Add validation and shadow blocks with default values
        this.addDefaultShadows();
      },
      
      addDefaultShadows: function() {
        // Add shadow blocks with appropriate default values for decomposition
        const inputs = ['X', 'Y', 'Z'];
        const defaultValues = [2, 2, 1]; // Common decomposition default
        
        inputs.forEach((inputName, index) => {
          const input = this.getInput(inputName);
          if (input) {
            const shadowBlock = this.workspace.newBlock(`${PREFIX}_value_number`);
            shadowBlock.setFieldValue(defaultValues[index], 'VALUE');
            shadowBlock.setShadow(true);
            shadowBlock.initSvg();
            shadowBlock.render();
            
            // Connect the shadow
            input.connection.connect(shadowBlock.outputConnection);
          }
        });
      },
      
      // Add validation for decomposition values
      validate: function() {
        const xInput = this.getInput('X');
        const yInput = this.getInput('Y');
        const zInput = this.getInput('Z');
        
        if (xInput && yInput && zInput) {
          const xBlock = xInput.connection.targetBlock();
          const yBlock = yInput.connection.targetBlock();
          const zBlock = zInput.connection.targetBlock();
          
          if (xBlock && yBlock && zBlock) {
            const x = parseInt(xBlock.getFieldValue('VALUE') || '1');
            const y = parseInt(yBlock.getFieldValue('VALUE') || '1');
            const z = parseInt(zBlock.getFieldValue('VALUE') || '1');
            
            // Validate that values are positive integers
            if (x < 1 || y < 1 || z < 1) {
              this.setWarningText('Decomposition values must be positive integers (≥ 1)');
              return false;
            }
            
            // Check if the product makes sense for decomposition
            const product = x * y * z;
            if (product > 1000) {
              this.setWarningText(`Total decomposition count (${product}) seems very high. Consider using fewer processors.`);
            } else if (product === 1) {
              this.setWarningText('Decomposition vector (1 1 1) means no parallelization');
            } else {
              this.setWarningText(null);
            }
            
            return true;
          }
        }
        return false;
      }
    };
  }

  // List value block
  const listValueType = `${PREFIX}_list_value`;
  if (!Blockly.Blocks[listValueType]) {
    Blockly.Blocks[listValueType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: listValueType,
          message0: '( %1 )',
          args0: [
            {
              type: 'input_statement',
              name: 'ITEMS'
            }
          ],
          output: 'List',
          colour: COLORS.VALUE,
          tooltip: 'List of items'
        });
      }
    };
  }

  // Patch list value
  const patchListType = `${PREFIX}_patch_list`;
  if (!Blockly.Blocks[patchListType]) {
    Blockly.Blocks[patchListType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: patchListType,
          message0: '( %1 )',
          args0: [
            {
              type: 'field_input',
              name: 'PATCHES',
              text: 'patch1 patch2'
            }
          ],
          output: 'List',
          colour: COLORS.VALUE,
          tooltip: 'List of patch names for structured decomposition'
        });
      }
    };
  }

  // Machine list value
  const machineListType = `${PREFIX}_machine_list`;
  if (!Blockly.Blocks[machineListType]) {
    Blockly.Blocks[machineListType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: machineListType,
          message0: '( %1 )',
          args0: [
            {
              type: 'field_input',
              name: 'MACHINES',
              text: 'machine1 machine2'
            }
          ],
          output: 'List',
          colour: COLORS.VALUE,
          tooltip: 'List of machine names for distributed cases'
        });
      }
    };
  }
}

// Register dropdown blocks
function registerDropdownBlocks(filename) {
  // Unified method dropdown (combines decomposition method and decomposer)
  const methodDropdownType = `${PREFIX}_method_dropdown`;
  if (!Blockly.Blocks[methodDropdownType]) {
    Blockly.Blocks[methodDropdownType] = {
      init: function() {
        this.jsonInit({
          type: methodDropdownType,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'METHOD',
              options: [
                ['scotch', 'scotch'],
                ['metis', 'metis'],
                ['simple', 'simple'],
                ['hierarchical', 'hierarchical'],
                ['manual', 'manual'],
                ['structured', 'structured'],
                ['multiLevel', 'multiLevel']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Decomposition method (works for both method and decomposer fields)'
        });
      }
    };
  }

  // Distributor dropdown
  const distributorDropdownType = `${PREFIX}_distributor_dropdown`;
  if (!Blockly.Blocks[distributorDropdownType]) {
    Blockly.Blocks[distributorDropdownType] = {
      init: function() {
        this.jsonInit({
          type: distributorDropdownType,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'DISTRIBUTOR',
              options: [
                ['distributed', 'distributed'],
                ['simple', 'simple'],
                ['ptscotch', 'ptscotch'],
                ['parMetis', 'parMetis']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Distribution method for parallel processing'
        });
      }
    };
  }

  // Boolean dropdown
  const booleanDropdownType = `${PREFIX}_boolean_dropdown`;
  if (!Blockly.Blocks[booleanDropdownType]) {
    Blockly.Blocks[booleanDropdownType] = {
      init: function() {
        this.jsonInit({
          type: booleanDropdownType,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'BOOLEAN',
              options: [
                ['false', 'false'],
                ['true', 'true']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Boolean value (true/false)'
        });
      }
    };
  }

  // Axis order dropdown for hierarchical method
  const axisOrderDropdownType = `${PREFIX}_axis_order_dropdown`;
  if (!Blockly.Blocks[axisOrderDropdownType]) {
    Blockly.Blocks[axisOrderDropdownType] = {
      init: function() {
        this.jsonInit({
          type: axisOrderDropdownType,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'ORDER',
              options: [
                ['xyz', 'xyz'],
                ['xzy', 'xzy'],
                ['yxz', 'yxz'],
                ['yzx', 'yzx'],
                ['zxy', 'zxy'],
                ['zyx', 'zyx']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Decomposition axis order for hierarchical method'
        });
      }
    };
  }
}

// Generate toolbox XML
function generateToolbox() {
  return `
<xml>
  <!-- Root blocks -->
  <block type="${PREFIX}_dict">
    <field name="DICT_NAME">decomposeParDict</field>
  </block>
  
  <block type="${PREFIX}_foamfile">
    <value name="VERSION">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">2.0</field>
      </shadow>
    </value>
    <value name="FORMAT">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">ascii</field>
      </shadow>
    </value>
    <value name="CLASS">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">dictionary</field>
      </shadow>
    </value>
    <value name="LOCATION">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">system</field>
      </shadow>
    </value>
    <value name="OBJECT">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">decomposeParDict</field>
      </shadow>
    </value>
  </block>
  
  <!-- Main keyword blocks -->
  <block type="${PREFIX}_numberOfSubdomains">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">4</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_method">
    <value name="VALUE">
      <shadow type="${PREFIX}_method_dropdown">
        <field name="METHOD">scotch</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_decomposer">
    <value name="VALUE">
      <shadow type="${PREFIX}_method_dropdown">
        <field name="METHOD">scotch</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_distributor">
    <value name="VALUE">
      <shadow type="${PREFIX}_distributor_dropdown">
        <field name="DISTRIBUTOR">distributed</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_distributed">
    <value name="VALUE">
      <shadow type="${PREFIX}_boolean_dropdown">
        <field name="BOOLEAN">false</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_roots">
    <value name="VALUE">
      <shadow type="${PREFIX}_machine_list">
        <field name="MACHINES">machine1 machine2</field>
      </shadow>
    </value>
  </block>
  
  <!-- Method coefficient sections -->
  <block type="${PREFIX}_simpleCoeffs_section"></block>
  <block type="${PREFIX}_hierarchicalCoeffs_section"></block>
  <block type="${PREFIX}_scotchCoeffs_section"></block>
  <block type="${PREFIX}_metisCoeffs_section"></block>
  <block type="${PREFIX}_manualCoeffs_section"></block>
  <block type="${PREFIX}_structuredCoeffs_section"></block>
  
  <!-- Generic keyValue block for all coefficient sections -->
  <block type="${PREFIX}_simpleCoeffs_keyValue">
    <field name="KEY">customKey</field>
    <value name="VALUE">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">value</field>
      </shadow>
    </value>
  </block>
  
  <!-- Value blocks -->
  <block type="${PREFIX}_value_string">
    <field name="VALUE">value</field>
  </block>
  
  <block type="${PREFIX}_value_number">
    <field name="VALUE">1</field>
  </block>
  
  <block type="${PREFIX}_decomp_vector">
    <value name="X">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">1</field>
      </shadow>
    </value>
    <value name="Y">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">1</field>
      </shadow>
    </value>
    <value name="Z">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">1</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_patch_list">
    <field name="PATCHES">patch1 patch2</field>
  </block>
  
  <block type="${PREFIX}_machine_list">
    <field name="MACHINES">machine1 machine2</field>
  </block>
  
  <!-- Dropdown blocks -->
  <block type="${PREFIX}_method_dropdown">
    <field name="METHOD">scotch</field>
  </block>
  
  <block type="${PREFIX}_distributor_dropdown">
    <field name="DISTRIBUTOR">distributed</field>
  </block>
  
  <block type="${PREFIX}_boolean_dropdown">
    <field name="BOOLEAN">false</field>
  </block>
  
  <block type="${PREFIX}_axis_order_dropdown">
    <field name="ORDER">xyz</field>
  </block>
</xml>`;
}

// Find matching block type for parsing
function findMatchingBlockType(component, dictName = 'decomposeParDict', path = '') {
  if (!component || typeof component !== 'object') {
    return null;
  }

  const { type, name, value, key } = component;
  const keyName = key || name;

  console.log(`Finding matching block type for component: type=${type}, key=${keyName} at path: ${path}`);

  if (type === 'dictionary') {
    if (name === 'FoamFile') {
      return `${PREFIX}_foamfile`;
    }
    
    // Check for method coefficient sections
    const methodCoeffPattern = /(\w+)Coeffs/;
    const match = name && name.match(methodCoeffPattern);
    if (match) {
      const methodName = match[1];
      if (Object.keys(METHOD_CONFIGS).includes(methodName)) {
        return `${PREFIX}_${name}_section`;
      }
    }
    
    return `${PREFIX}_dict`;
  }

  if (type === 'keyValue') {
    // Check for main keywords
    const mainKeyword = KEYWORD_CONFIGS.find(cfg => cfg.key === keyName);
    if (mainKeyword) {
      return `${PREFIX}_${keyName}`;
    }
    
    // Check for method coefficient keywords - try specific blocks first
    for (const [methodName, coeffs] of Object.entries(METHOD_CONFIGS)) {
      const coeffConfig = coeffs.find(cfg => cfg.key === keyName);
      if (coeffConfig) {
        const specificBlockType = `${PREFIX}_${methodName}Coeffs_${keyName}`;
        // Check if specific block exists, otherwise use generic keyValue
        if (Blockly.Blocks[specificBlockType]) {
          return specificBlockType;
        } else {
          return `${PREFIX}_${methodName}Coeffs_keyValue`;
        }
      }
    }
    
    // Check if this is within a coefficient section context from path
    const coeffSectionMatch = path.match(/(\w+)Coeffs/);
    if (coeffSectionMatch) {
      const methodName = coeffSectionMatch[1];
      if (Object.keys(METHOD_CONFIGS).includes(methodName)) {
        return `${PREFIX}_${methodName}Coeffs_keyValue`;
      }
    }
    
    // Fallback to common blocks
    return null;
  }

  return null;
}

// Export dictionary name and prefix for registry
export default {
  dictName: 'decomposeParDict',
  prefix: PREFIX,
  registerBlocks,
  generateToolbox,
  getBlocks: () => Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)),
  findMatchingBlockType: (component, dictName, path) => findMatchingBlockType(component, dictName, path),
  createBlock: createDecomposeParDictBlock,
  createKeywordBlock: createKeywordBlock
};

// Create decomposeParDict block
export function createDecomposeParDictBlock(workspace, entries = [], dictName = 'decomposeParDict') {
  try {
    const blockType = dictName === 'decomposeParDict' ? `${PREFIX}_dict` : 'common_dict';
    const block = createBlock(workspace, blockType);
    if (!block) {
      console.error(`Failed to create ${dictName} block`);
      return null;
    }

    const entriesInput = block.getInput('ENTRIES');
    if (entriesInput && entries.length > 0) {
      let prevBlock = null;
      
      for (const entry of entries) {
        const entryBlock = createBlockFromComponent(entry, workspace, dictName);
        if (entryBlock) {
          // Set values for key-value pairs
          if (entry.type === 'keyValue') {
            if (entryBlock.getField('KEY')) {
              entryBlock.setFieldValue(entry.key, 'KEY');
            }
            
            // Set value if it's a simple value
            if (typeof entry.value !== 'object') {
              const valueBlock = createBlock(workspace, 
                typeof entry.value === 'number' ? 'common_value_number' : 'common_value_string');
              if (valueBlock) {
                valueBlock.setFieldValue(String(entry.value), 'VALUE');
                connectValueBlock(entryBlock, valueBlock, entry.key);
              }
            }
          }

          // Connect blocks
          if (!prevBlock) {
            entriesInput.connection.connect(entryBlock.previousConnection);
          } else if (prevBlock.nextConnection && entryBlock.previousConnection) {
            prevBlock.nextConnection.connect(entryBlock.previousConnection);
          }
          prevBlock = entryBlock;
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating block for ${dictName}:`, error);
    return null;
  }
}

// Create keyword block
export function createKeywordBlock(workspace, key, value) {
  try {
    console.log(`Creating keyword block for ${key}:`, value);
    
    // Find the appropriate config
    let config = KEYWORD_CONFIGS.find(cfg => cfg.key === key);
    let blockType = `${PREFIX}_${key}`;
    
    // If not found in main keywords, check method coefficients
    if (!config) {
      for (const [methodName, coeffs] of Object.entries(METHOD_CONFIGS)) {
        const coeffConfig = coeffs.find(cfg => cfg.key === key);
        if (coeffConfig) {
          config = coeffConfig;
          blockType = `${PREFIX}_${methodName}Coeffs_${key}`;
          break;
        }
      }
    }
    
    if (!config) {
      console.error(`No configuration found for keyword: ${key}`);
      return null;
    }

    const block = createBlock(workspace, blockType);
    if (!block) {
      console.error(`Failed to create block for keyword: ${key}`);
      return null;
    }

    let valueBlock;
    const validatedValue = config.validator(value);
    
    valueBlock = createBlock(workspace, config.shadow);
    if (valueBlock) {
      if (config.shadow.includes('decomp_vector') || config.shadow.includes('vector3') || config.shadow.includes('vector') || config.isVector3) {
        // Handle all vector types using specialized decomp_vector - consolidated handling
        const vectorMatch = String(validatedValue).match(/\(\s*(\d+)\s+(\d+)\s+(\d+)\s*\)/);
        if (vectorMatch) {
          const xBlock = createBlock(workspace, `${PREFIX}_value_number`);
          const yBlock = createBlock(workspace, `${PREFIX}_value_number`);
          const zBlock = createBlock(workspace, `${PREFIX}_value_number`);
          
          if (xBlock && yBlock && zBlock) {
            xBlock.setFieldValue(parseInt(vectorMatch[1]), 'VALUE');
            yBlock.setFieldValue(parseInt(vectorMatch[2]), 'VALUE');
            zBlock.setFieldValue(parseInt(vectorMatch[3]), 'VALUE');
            
            valueBlock.getInput('X').connection.connect(xBlock.outputConnection);
            valueBlock.getInput('Y').connection.connect(yBlock.outputConnection);
            valueBlock.getInput('Z').connection.connect(zBlock.outputConnection);
          }
        }
      } else {
        valueBlock.setFieldValue(String(validatedValue), 'VALUE');
      }
    }

    if (valueBlock) {
      connectValueBlock(block, valueBlock, key);
    }

    return block;
  } catch (error) {
    console.error(`Error creating keyword block for ${key}:`, error);
    return null;
  }
}

// Helper function to create blocks from components
function createBlockFromComponent(component, workspace, dictName) {
  const blockType = findMatchingBlockType(component, dictName);
  if (!blockType) {
    return null; // Fall back to common blocks
  }

  const block = createBlock(workspace, blockType);
  if (!block) {
    return null;
  }

  // Set key field if it exists
  const keyValue = component.key || component.name;
  if (keyValue && block.getField('KEY')) {
    block.setFieldValue(keyValue, 'KEY');
  }

  return block;
}